import React, { useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ActionMenuProps } from '@lyearn/molecules';
import { Stack } from '@lyearn/ui';
import useWorkspaceAccessControl from '@/components/WorkspaceAccessControl/hooks/useWorkspaceAccessControl';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import * as AdminEntity from '@/modules/corporate/modules/admin/components/AdminEntity';
import { EventStatusEnum, EventUnion, RecurringTypeEnum, WorkspaceSubjects } from '@/types/schema';

import { getIsRecurringSession } from '../../../../utils';
import { useEventActionType } from '../../hooks/useEventAction';
import { getEventActionItems } from '../../utils';
import ListItemContent from './ListItemContent';
import RecurringEventsCount from './RecurringEventsCount';
import StatusLabel from './StatusLabel';
import UsersRegisteredLabel from './UsersRegisteredLabel';

const LYEARN_CONNECT_ALLOWED_EVENT_STATUS = [
  EventStatusEnum.Waiting,
  EventStatusEnum.Scheduled,
  EventStatusEnum.Completed,
];

function ListItem({
  Image,
  event,
  url,
  onAction,
}: {
  Image: JSX.Element;
  event: EventUnion;
  url: string;
  onAction: useEventActionType['methods']['onAction'];
}) {
  const { t } = useTranslation();
  const { status, usersCount } = event;
  const { [FeatureFlags.EnableLyearnConnect]: enableLyearnConnect } = useFeatureFlags();
  const currentUser = useUserContext();

  // Showing LyearnConnect if feature flag is enabled and event is not already shared by workspace and event status is waiting or scheduled
  const showLyearnConnect =
    enableLyearnConnect &&
    !event.sharedByWorkspace &&
    LYEARN_CONNECT_ALLOWED_EVENT_STATUS.includes(status);

  const { isAccessible: isWorkspaceAccessible } = useWorkspaceAccessControl();

  const actionMenu = useMemo(
    () =>
      ({
        items: getEventActionItems({
          t,
          showLyearnConnect,
          isWorkspaceAccessible,
          event,
          isDetailPage: false,
          currentUser,
        }),
        onAction,
      }) as {
        items: ActionMenuProps['items'];
        onAction: ActionMenuProps['onAction'];
      },
    [t, showLyearnConnect, isWorkspaceAccessible, event, onAction, currentUser],
  );

  return (
    <AdminEntity.ListItem
      actionMenu={actionMenu}
      statusLabel={
        <Stack className="space-s-8">
          <StatusLabel status={status} />
          <UsersRegisteredLabel status={status} usersCount={usersCount} />
          {!getIsRecurringSession(event.recurringCategory) ? null : (
            <RecurringEventsCount eventsCount={event.recurringEventSlotCount ?? 0} />
          )}
        </Stack>
      }
      url={url}
      workspaceSubject={WorkspaceSubjects.Event}>
      <ListItemContent
        Image={Image}
        event={event}
        showRecurringIcon={event.recurringCategory === RecurringTypeEnum.RecurringInstance}
      />
    </AdminEntity.ListItem>
  );
}

export default ListItem;
